import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import "./Settings.css";

const Settings = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    if (currentUser?.id) {
      fetch(`${process.env.REACT_APP_API_URL}/api/get-subscription/${currentUser.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.planName) setSubscriptionInfo(data);
        })
        .catch((err) => console.error("❌ Error fetching subscription:", err));
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // ✅ NEW: Delete account from Settings (uses the exact same route as ProfileEditDialog)
  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        "⚠ Are you sure you want to permanently delete your account? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser?.id) {
        alert("User not found in local storage.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${storedUser.id}`,
        { method: "DELETE", credentials: "include" }
      );

      let data;
      try {
        data = await response.json();
      } catch (e) {
        const txt = await response.text().catch(() => "");
        console.error("❌ Delete response not JSON:", txt);
        alert("Account deletion failed (unexpected server response).");
        return;
      }

      if (data?.success) {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("token"); // safe even if you don’t use it
        alert("Your account has been permanently deleted.");
        window.location.href = "/"; // redirect to homepage/login
      } else {
        alert(data?.error || "Failed to delete account.");
      }
    } catch (err) {
      console.error("❌ Error deleting account:", err);
      alert("Something went wrong.");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/backend/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Server logout request failed:", error);
    } finally {
      localStorage.removeItem("currentUser");
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  };

  const lowerSearch = searchTerm.toLowerCase();

  const sections = [
    {
      key: "profile",
      title: "👤 Profile Management",
      description: "Update your public information and account settings.",
      content: (
        <NavLink to="/profile" className="settings-link">
          ⚙️ Edit My Profile
        </NavLink>
      ),
    },

    // ✅ NEW SECTION: Apple-friendly location in Settings
    {
      key: "account",
      title: "🔐 Account",
      description: "Manage your account and permanently delete it if needed.",
      content: (
        <button
          type="button"
          onClick={handleDeleteAccount}
          className="delete-account-btn"
          style={{
            width: "fit-content",
            padding: "10px 14px",
            borderRadius: "6px",
            border: "1px solid #c0392b",
            background: "#fff",
            color: "#c0392b",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          ❌ Delete Account
        </button>
      ),
    },

    {
      key: "support",
      title: "🛟 Support & Help",
      description: "Get help from our team for account or app-related issues.",
      content: (
        <a href="mailto:support@scoldy.com" className="settings-link">
          ✉️ Contact Support
        </a>
      ),
    },
    {
      key: "subscription",
      title: "💎 Premium Subscription",
      description: "Manage or upgrade your Scoldy Premium plan.",
      content: (
        <>
          <div className="subscription-actions">
            <a
              href="https://www.paypal.com/myaccount/autopay/"
              target="_blank"
              rel="noopener noreferrer"
              className="manage-subscription-button"
            >
              Manage My Subscription
            </a>

            <NavLink to="/cloud9" className="upgrade-subscription-button">
              Upgrade My Plan
            </NavLink>
          </div>

          {subscriptionInfo ? (
            <div className="active-plan-box">
              <h3>🌟 Current Subscription Details</h3>
              <p>
                <strong>Plan:</strong> {subscriptionInfo.planName} Plan
              </p>
              <p>
                <strong>ID:</strong> {subscriptionInfo.subscriptionId}
              </p>
              <p>
                <strong>Status:</strong> {subscriptionInfo.status}
              </p>
              <p>
                <strong>Subscribed On:</strong> {formatDate(subscriptionInfo.createdAt)}
              </p>
            </div>
          ) : (
            <div className="no-subscription-text">
              <p style={{ color: "#888", fontStyle: "italic", marginTop: "10px" }}>
                You currently have no active subscription.
              </p>
            </div>
          )}
        </>
      ),
    },
    {
      key: "logout",
      title: "Log Out",
      description: "End your current Scoldy session on this device.",
      content: (
        <button
          type="button"
          onClick={handleLogout}
          className="settings-logout-button"
        >
          Log Out
        </button>
      ),
    },
  ];

  const filteredSections = sections.filter(
    (section) =>
      section.title.toLowerCase().includes(lowerSearch) ||
      section.description.toLowerCase().includes(lowerSearch)
  );

  return (
    <div className="settings-container">
        <h1 className="settings-header">Scoldy Settings</h1>
        <p className="settings-subtitle">
          Manage your preferences, support requests, and subscriptions.
        </p>

        <div className="settings-search">
          <input
            type="text"
            className="settings-search-box"
            placeholder="Search settings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="settings-grid">
          {filteredSections.length > 0 ? (
            filteredSections.map((section) => (
              <div key={section.key} className="settings-card">
                <h2>{section.title}</h2>
                <p className="settings-desc">{section.description}</p>
                <div className="settings-content">{section.content}</div>
              </div>
            ))
          ) : (
            <p style={{ color: "#888", marginTop: "20px" }}>❌ No results found.</p>
          )}
        </div>
    </div>
  );
};

export default Settings;
